import React, { useRef, useEffect } from 'react';
import TreeSelect from '../TreeSelect/TreeSelect';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import Select from 'components/UI/V3/Forms/Select';
import PhoneField3 from 'components/UI/PhoneField3';
import Button from 'components/UI/V3/Button/Button';
import styles from './OrganizationForm.module.scss';
import { SaveOrganization } from 'model/organization';

interface OrganizationFormDataProps {
    isParentOrganization: boolean;
    formData: SaveOrganization;
    parentOrganizations: {
        label: string;
        value: string;
        parentOrganizationId: string | null | undefined;
    }[];
    businessTypeOptions: {
        label: string;
        value: string;
    }[];
    errors: Partial<SaveOrganization>;
    editingAccount: SaveOrganization | null;
    isSaving: boolean;
    onPhoneBlur: () => void;
    onSave: () => Promise<void>;
    onInputChange: (field: keyof SaveOrganization, value: string | boolean | number | undefined) => void;
}

export const OrganizationForm = ({
    isParentOrganization,
    formData,
    businessTypeOptions,
    onInputChange,
    onPhoneBlur,
    onSave,
    parentOrganizations,
    errors,
    editingAccount,
    isSaving
}: OrganizationFormDataProps) => {
    // Create refs for all input fields
    const parentOrganizationIdRef = useRef<HTMLInputElement>(null);
    const nameRef = useRef<HTMLInputElement>(null);
    const nicknameDbaRef = useRef<HTMLInputElement>(null);
    const taxIdRef = useRef<HTMLInputElement>(null);
    const businessTypeRef = useRef<HTMLDivElement>(null); // Wrapper div ref for Select
    const emailRef = useRef<HTMLInputElement>(null);
    const phoneRef = useRef<HTMLInputElement>(null);
    const websiteRef = useRef<HTMLInputElement>(null);
    const addressLineOneRef = useRef<HTMLInputElement>(null);
    const addressLineTwoRef = useRef<HTMLInputElement>(null);
    const cityRef = useRef<HTMLInputElement>(null);
    const stateRef = useRef<HTMLInputElement>(null);
    const zipcodeRef = useRef<HTMLInputElement>(null);
    const countryRef = useRef<HTMLInputElement>(null);

    // Track the last error keys to avoid refocusing on the same errors
    const lastErrorKeysRef = useRef<string>('');

    // Focus on the first field with an error when errors change
    useEffect(() => {
        if (!errors || Object.keys(errors).length === 0) {
            // Reset tracking when errors are cleared
            lastErrorKeysRef.current = '';
            return;
        }

        // Create a string representation of current error keys
        const currentErrorKeys = Object.keys(errors).sort().join(',');

        // Only focus if this is a new set of errors (different from last time)
        if (currentErrorKeys === lastErrorKeysRef.current) {
            return;
        }

        // Update the last error keys
        lastErrorKeysRef.current = currentErrorKeys;

        // Define the order of fields to check for errors
        const fieldRefs: Array<{
            key: keyof SaveOrganization;
            ref: React.RefObject<HTMLInputElement | HTMLDivElement>;
        }> = [
            { key: 'parentOrganizationId', ref: parentOrganizationIdRef },
            { key: 'name', ref: nameRef },
            { key: 'nicknameDba', ref: nicknameDbaRef },
            { key: 'taxId', ref: taxIdRef },
            { key: 'businessType', ref: businessTypeRef },
            { key: 'email', ref: emailRef },
            { key: 'phone', ref: phoneRef },
            { key: 'website', ref: websiteRef },
            { key: 'addressLineOne', ref: addressLineOneRef },
            { key: 'addressLineTwo', ref: addressLineTwoRef },
            { key: 'city', ref: cityRef },
            { key: 'state', ref: stateRef },
            { key: 'zipcode', ref: zipcodeRef },
            { key: 'country', ref: countryRef }
        ];

        // Find the first field with an error and focus it
        for (const { key, ref } of fieldRefs) {
            if (errors[key] && ref.current) {
                // Try to focus if it's an HTMLInputElement, otherwise scroll into view
                if ('focus' in ref.current && typeof ref.current.focus === 'function') {
                    ref.current.focus();
                } else {
                    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                break;
            }
        }
    }, [errors]);

    return (
        <form
            className={styles.form}
            onSubmit={e => e.preventDefault()}
        >
            {!isParentOrganization && (
                <>
                    <h2 className={styles.modalTitle}>
                        {editingAccount ? 'Edit organization' : 'Add an organization'}
                    </h2>

                    <div className={styles.formField}>
                        <TreeSelect
                            ref={parentOrganizationIdRef}
                            label="Parent organization"
                            options={parentOrganizations}
                            value={isParentOrganization ? undefined : formData.parentOrganizationId?.toString()}
                            onChange={value => onInputChange('parentOrganizationId', value)}
                            placeholder="Select an organization"
                            error={errors.parentOrganizationId?.toString()}
                            required
                        />
                    </div>
                </>
            )}
            {/* Legal business name */}
            <div className={styles.formField}>
                <Input
                    ref={nameRef}
                    id="name"
                    label="Legal business name"
                    value={formData.name}
                    onChange={e => onInputChange('name', e.target.value)}
                    placeholder="Enter business name"
                    error={errors.name}
                />
            </div>

            {/* Nickname/DBA and EIN Row */}
            <div className={styles.formRow}>
                <div className={styles.formField}>
                    <Input
                        ref={nicknameDbaRef}
                        id="nicknameDba"
                        label="Nickname / DBA"
                        value={formData.nicknameDba}
                        onChange={e => onInputChange('nicknameDba', e.target.value)}
                        placeholder="Enter nickname / DBA"
                        error={errors.nicknameDba}
                    />
                </div>
                <div className={styles.formField}>
                    <Input
                        ref={taxIdRef}
                        id="taxId"
                        label="EIN"
                        value={formData.taxId}
                        onChange={e => onInputChange('taxId', e.target.value)}
                        placeholder="Enter EIN"
                        error={errors.taxId}
                    />
                </div>
            </div>

            {/* Business type */}
            <div
                className={styles.formField}
                ref={businessTypeRef}
            >
                <Select
                    label="Business type"
                    options={businessTypeOptions}
                    value={formData.businessType}
                    onChange={(value: string) => onInputChange('businessType', value)}
                    placeholder="Select business type"
                    error={errors.businessType}
                />
            </div>

            {/* Work Email and Phone Number Row */}
            <div className={styles.formRow}>
                <div className={styles.formField}>
                    <Input
                        ref={emailRef}
                        id="email"
                        label="Work email"
                        type="email"
                        value={formData.email}
                        onChange={e => onInputChange('email', e.target.value)}
                        placeholder="Enter work email"
                        error={errors.email}
                    />
                </div>
                <div className={styles.formField}>
                    <PhoneField3
                        ref={phoneRef}
                        id="phone"
                        name="phone"
                        label="Phone number"
                        value={formData.phone || ''}
                        onChange={value => onInputChange('phone', value)}
                        onBlur={onPhoneBlur}
                        placeholder="(___) ___-____"
                        errors={errors.phone ? [{ fieldName: 'phone', errorMessage: errors.phone }] : undefined}
                    />
                </div>
            </div>

            {/* Website */}
            <div className={styles.formField}>
                <Input
                    ref={websiteRef}
                    id="website"
                    label="Website"
                    type="text"
                    value={formData.website}
                    onChange={e => onInputChange('website', e.target.value)}
                    placeholder="Enter website URL"
                    error={errors.website}
                />
            </div>

            {/* Address Line 1 */}
            <div className={styles.formField}>
                <Input
                    ref={addressLineOneRef}
                    id="addressLineOne"
                    label="Address line 1"
                    value={formData.addressLineOne}
                    onChange={e => onInputChange('addressLineOne', e.target.value)}
                    placeholder="Enter address line 1"
                    error={errors.addressLineOne}
                />
            </div>

            {/* Address Line 2 */}
            <div className={styles.formField}>
                <Input
                    ref={addressLineTwoRef}
                    id="addressLineTwo"
                    label="Address line 2"
                    value={formData.addressLineTwo}
                    onChange={e => onInputChange('addressLineTwo', e.target.value)}
                    placeholder="Enter address line 2 (optional)"
                    error={errors.addressLineTwo}
                />
            </div>

            {/* City, State, Zipcode, Country Row */}
            <div className={styles.formRow}>
                <div className={styles.formField}>
                    <Input
                        ref={cityRef}
                        id="city"
                        label="City"
                        value={formData.city}
                        onChange={e => onInputChange('city', e.target.value)}
                        placeholder="City"
                        error={errors.city}
                    />
                </div>
                <div className={styles.formField}>
                    <Input
                        ref={stateRef}
                        id="state"
                        label="State"
                        value={formData.state}
                        onChange={e => onInputChange('state', e.target.value)}
                        placeholder="State"
                        error={errors.state}
                    />
                </div>
                <div className={styles.formField}>
                    <Input
                        ref={zipcodeRef}
                        id="zipcode"
                        label="Zipcode"
                        value={formData.zipcode}
                        onChange={e => onInputChange('zipcode', e.target.value)}
                        placeholder="Zipcode"
                        error={errors.zipcode}
                    />
                </div>
                <div className={styles.formField}>
                    <Input
                        ref={countryRef}
                        id="country"
                        label="Country"
                        value={formData.country}
                        onChange={e => onInputChange('country', e.target.value)}
                        placeholder="Country"
                        error={errors.country}
                    />
                </div>
            </div>

            {/* Save Button */}
            <div className={styles.formActions}>
                <Button
                    label={isSaving ? 'Saving...' : 'Save'}
                    variant="black"
                    size="large"
                    onClick={onSave}
                    disabled={isSaving}
                />
            </div>
        </form>
    );
};

export default OrganizationForm;
